import { Injectable, Provider, computed, inject, signal } from '@angular/core';
import { BrowserStorageService } from '@app/services/browser-storage.service';
import { TpvSelectedMerchantReset } from '@app/domain/ports/tpv/tpv-selected-merchant-reset.port';

/** Comercio actualmente seleccionado para las lecturas por-comercio de la fachada. */
export interface SelectedMerchant {
  /** UUID del comercio (external_id_merchant). */
  id: string;
  /** Nombre comercial para mostrar (puede llegar vacío en un drill por UUID). */
  name: string;
}

const STORAGE_KEY = 'tpv.selectedMerchant';

/**
 * Estado compartido del comercio seleccionado en el TPV.
 *
 * La fachada `api/v1/tpv/` lee transacciones/dispersiones/liquidaciones/terminales
 * POR comercio (exige `id_merchant`). Este servicio guarda el comercio elegido una
 * sola vez y lo comparte entre módulos para no re-seleccionar al navegar. Se
 * persiste en sessionStorage (aislamiento por pestaña, igual que la sesión).
 *
 * El dashboard de TPV también lo respeta al montarse (2026-10-19). Lo único que lo
 * limpia desde fuera de TPV es `/products` (R19/R20 de la feature 152), por el puerto
 * `TpvSelectedMerchantReset`.
 */
@Injectable({ providedIn: 'root' })
export class SelectedMerchantService extends TpvSelectedMerchantReset {
  private readonly storage = inject(BrowserStorageService);

  private readonly _selected = signal<SelectedMerchant | null>(this.restore());

  /** Comercio seleccionado (signal de solo lectura). */
  readonly selected = this._selected.asReadonly();
  /** UUID del comercio seleccionado, o `null`. */
  readonly selectedId = computed(() => this._selected()?.id ?? null);

  /** Selecciona un comercio y lo persiste. */
  select(merchant: SelectedMerchant): void {
    this._selected.set(merchant);
    this.persist(merchant);
  }

  /**
   * Fija el comercio por UUID conservando el nombre previo si coincide (caso drill,
   * donde solo llega el UUID). Evita pisar el nombre ya conocido con vacío.
   */
  selectById(id: string, name = ''): void {
    if (!id) return;
    const current = this._selected();
    const resolvedName = name || (current?.id === id ? current.name : '');
    this.select({ id, name: resolvedName });
  }

  /** Actualiza solo el nombre del comercio seleccionado (cuando el selector lo resuelve). */
  setName(id: string, name: string): void {
    const current = this._selected();
    if (current?.id === id && name && current.name !== name) {
      this.select({ id, name });
    }
  }

  clear(): void {
    this._selected.set(null);
    this.storage.removeSessionStorageItem(STORAGE_KEY);
  }

  private persist(merchant: SelectedMerchant): void {
    try {
      this.storage.setSessionStorageItem(STORAGE_KEY, JSON.stringify(merchant));
    } catch {
      // Persistir es best-effort; el estado en memoria sigue siendo la fuente de verdad.
    }
  }

  private restore(): SelectedMerchant | null {
    const raw = this.storage.getSessionStorageItem(STORAGE_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as SelectedMerchant;
      return parsed?.id ? parsed : null;
    } catch {
      return null;
    }
  }
}

/**
 * Enlace del puerto que usa `/products` para limpiar el comercio. Va en la raíz
 * (`app.config.ts`) y con `useExisting`: tiene que ser el MISMO singleton que leen
 * las pantallas de TPV, o limpiaría una copia que nadie mira.
 */
export function provideTpvSelectedMerchantReset(): Provider[] {
  return [{ provide: TpvSelectedMerchantReset, useExisting: SelectedMerchantService }];
}
